import React, { useState, useEffect } from "react";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import Header from "./Header";
import { Pagination } from "react-bootstrap";
import { FaEye, FaPen, FaHistory, FaPlus } from "react-icons/fa";
import { Search, Download, UserPlus } from "lucide-react";
import Loader from "./Loader.js";
import GreyArrow from "../assets/greyLeftAr.png";
import { Modal, Button } from "react-bootstrap";
import {
  server_post_data,
  get_all_plans,
  save_update_custom_plan,
  update_plan_features,
  assign_standard_plan,
  delete_custom_plan,
  extend_plan_duration,
  get_restaurant_plan_history,
  get_restaurant_list,
} from "../ServiceConnection/serviceconnection.js";
import {
  handleError,
  handleSuccess,
  computeTodayDate,
  computeplussevendays,
} from "../CommonJquery/CommonJquery";
import "./Css/WebsiteMngmt.css";

// ─── Constants ────────────────────────────────────────────────────────────────

const FEATURES = [
  {
    key: "pos_billing",
    label: "POS Billing System",
    plan: "free",
    locked: true,
  },
  { key: "gst_invoicing", label: "GST Invoicing", plan: "free", locked: true },
  { key: "basic_reports", label: "Basic Reports", plan: "free", locked: true },
  { key: "email_support", label: "Email Support", plan: "free", locked: true },
  { key: "mobile_pos", label: "Mobile POS", plan: "free", locked: true },
  { key: "single_outlet", label: "Single Outlet", plan: "free", locked: true },
  {
    key: "google_business",
    label: "Google Business Profile",
    plan: "free",
    locked: true,
  },
  {
    key: "offline_mode",
    label: "Offline Mode",
    plan: "starter",
    locked: false,
  },
  {
    key: "inventory_management",
    label: "Inventory Management",
    plan: "starter",
    locked: false,
  },
  {
    key: "staff_management",
    label: "Staff Management",
    plan: "starter",
    locked: false,
  },
  {
    key: "kot_printing",
    label: "KOT Printing",
    plan: "starter",
    locked: false,
  },
  {
    key: "advanced_analytics",
    label: "Advanced Analytics",
    plan: "starter",
    locked: false,
  },
  {
    key: "swiggy_zomato",
    label: "Swiggy / Zomato Integration",
    plan: "starter",
    locked: false,
  },
  { key: "captain_app", label: "Captain App", plan: "starter", locked: false },
  { key: "manager_app", label: "Manager App", plan: "starter", locked: false },
  {
    key: "priority_support",
    label: "Priority Support",
    plan: "starter",
    locked: false,
  },
  {
    key: "qr_ordering",
    label: "QR Ordering",
    plan: "professional",
    locked: false,
  },
  {
    key: "online_ordering_website",
    label: "Online Ordering Website",
    plan: "professional",
    locked: false,
  },
  {
    key: "central_kitchen",
    label: "Central Kitchen Management",
    plan: "professional",
    locked: false,
  },
  {
    key: "dedicated_account_manager",
    label: "Dedicated Account Manager",
    plan: "professional",
    locked: false,
  },
];

const PLAN_BADGE = {
  free: { bg: "#e8f5e9", color: "#2e7d32", border: "#66bb6a", label: "Free" },
  starter: {
    bg: "#fff8e1",
    color: "#f57f17",
    border: "#ffca28",
    label: "Starter",
  },
  professional: {
    bg: "#e3f2fd",
    color: "#1565c0",
    border: "#42a5f5",
    label: "Professional",
  },
  custom: {
    bg: "#f3e5f5",
    color: "#6a1b9a",
    border: "#ab47bc",
    label: "Custom",
  },
};

const initFeatures = (data = {}) => {
  const obj = {};
  FEATURES.forEach((f) => {
    obj[f.key] = data[f.key] !== undefined ? data[f.key] : f.locked ? 1 : 0;
  });
  return obj;
};

const convertToDate = (dateString) => {
  if (!dateString) return new Date();
  const [year, month, day] = dateString.split("-").map(Number);
  return new Date(year, month - 1, day);
};

const formatDate = (date) => {
  if (!date) return "";
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
};

// ─── Component ────────────────────────────────────────────────────────────────

function PlanManagement() {
  const [showLoaderAdmin, setshowLoaderAdmin] = useState(false);

  // Data
  const [standardPlanList, setStandardPlanList] = useState([]);
  const [customPlanList, setCustomPlanList] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [historyData, setHistoryData] = useState([]);
  const [searchfilter, setSearchFilter] = useState("");
  const [restaurantOptions, setRestaurantOptions] = useState([]); // [{id, name}]

  // View state
  const [activeTab, setActiveTab] = useState("custom"); // "custom" | "standard" | "history"
  const [historyResId, setHistoryResId] = useState("");

  // Date filter
  const [startDate, setStartDate] = useState(
    convertToDate(computeplussevendays()),
  );
  const [endDate, setEndDate] = useState(convertToDate(computeTodayDate()));

  // Pagination
  const itemsPerPage = 10;
  const [currentPage, setCurrentPage] = useState(1);

  // ── Modals ──
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [showExtendModal, setShowExtendModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [showFeatureModal, setShowFeatureModal] = useState(false);

  // ── Form state — Create/Edit Custom Plan ──
  const [editPlanId, setEditPlanId] = useState("0");
  const [formRestaurantId, setFormRestaurantId] = useState("");
  const [formPlanName, setFormPlanName] = useState("");
  const [formPrice, setFormPrice] = useState("");
  const [formDuration, setFormDuration] = useState("365");
  const [formStartDate, setFormStartDate] = useState("");
  const [formMaxOutlets, setFormMaxOutlets] = useState("1");
  const [formFeatures, setFormFeatures] = useState(initFeatures());
  const [isEditMode, setIsEditMode] = useState(false);

  // ── Form state — Assign Standard Plan ──
  const [assignResId, setAssignResId] = useState("");
  const [assignPlanName, setAssignPlanName] = useState("free");
  const [assignStart, setAssignStart] = useState("");

  // ── Form state — Extend Plan ──
  const [extendResId, setExtendResId] = useState("");
  const [extendDays, setExtendDays] = useState("");

  // ── Delete ──
  const [deletePlanId, setDeletePlanId] = useState("");

  // ── Detail view ──
  const [selectedPlan, setSelectedPlan] = useState(null);

  // ── Feature toggle ──
  const [featurePlanId, setFeaturePlanId] = useState("");
  const [featureState, setFeatureState] = useState(initFeatures());
  const [origFeatures, setOrigFeatures] = useState(initFeatures());

  // ─── Fetch ───────────────────────────────────────────────────────────
  useEffect(() => {
    master_data_get();
    fetch_restaurants();
  }, []);

  const fetch_restaurants = async () => {
    const fd = new FormData();
    await server_post_data(get_restaurant_list, fd)
      .then((Response) => {
        if (!Response.data.error) {
          const list = Response.data.message.reservation_list || [];
          setRestaurantOptions(
            list.map((r) => ({ id: r.primary_id, name: r.restaurant_name })),
          );
        }
      })
      .catch(() => {});
  };

  const master_data_get = async () => {
    setshowLoaderAdmin(true);
    const fd = new FormData();
    fd.append("restaurant_id", "");
    await server_post_data(get_all_plans, fd)
      .then((Response) => {
        setshowLoaderAdmin(false);
        if (Response.data.error) {
          handleError(Response.data.message);
          return;
        }
        const msg = Response.data.message;
        setStandardPlanList(msg.standard_plans || []);
        setCustomPlanList(msg.custom_plans || []);
        setFilteredData(msg.custom_plans || []);
      })
      .catch(() => {
        handleError("network");
        setshowLoaderAdmin(false);
      });
  };

  const get_history = async (restaurant_id) => {
    setshowLoaderAdmin(true);
    const fd = new FormData();
    fd.append("restaurant_id", restaurant_id);
    await server_post_data(get_restaurant_plan_history, fd)
      .then((Response) => {
        setshowLoaderAdmin(false);
        if (Response.data.error) {
          handleError(Response.data.message);
          return;
        }
        setHistoryData(Response.data.message.history || []);
        setHistoryResId(restaurant_id);
        setActiveTab("history");
      })
      .catch(() => {
        handleError("network");
        setshowLoaderAdmin(false);
      });
  };

  // ─── Search ──────────────────────────────────────────────────────────
  const handleSearch = (e) => {
    const val = e.target.value;
    setSearchFilter(val);
    const src = activeTab === "standard" ? standardPlanList : customPlanList;
    setFilteredData(
      src.filter((row) =>
        Object.values(row).some(
          (v) =>
            v !== null &&
            v !== undefined &&
            (typeof v === "string" ? v : v.toString())
              .toLowerCase()
              .includes(val.toLowerCase()),
        ),
      ),
    );
    setCurrentPage(1);
  };

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    setSearchFilter("");
    setCurrentPage(1);
    if (tab === "standard") setFilteredData(standardPlanList);
    else if (tab === "custom") setFilteredData(customPlanList);
  };

  // ─── Pagination ──────────────────────────────────────────────────────
  const currentSource = activeTab === "history" ? historyData : filteredData;
  const indexOfLast = currentPage * itemsPerPage;
  const indexOfFirst = indexOfLast - itemsPerPage;
  const currentItems = currentSource.slice(indexOfFirst, indexOfLast);
  const totalPages = Math.ceil(currentSource.length / itemsPerPage);

  // ─── Stats ───────────────────────────────────────────────────────────
  const stats = {
    total: customPlanList.length,
    free: customPlanList.filter((p) => p.plan_name === "free").length,
    starter: customPlanList.filter((p) => p.plan_name === "starter").length,
    professional: customPlanList.filter((p) => p.plan_name === "professional")
      .length,
    custom: customPlanList.filter((p) => p.plan_name === "custom").length,
  };

  // ─── Reset Create Form ───────────────────────────────────────────────
  const resetForm = () => {
    setEditPlanId("0");
    setFormRestaurantId("");
    setFormPlanName("");
    setFormPrice("");
    setFormDuration("365");
    setFormStartDate("");
    setFormMaxOutlets("1");
    setFormFeatures(initFeatures());
    setIsEditMode(false);
  };

  // ─── Open Edit ───────────────────────────────────────────────────────
  const handleEdit = (plan) => {
    setEditPlanId(String(plan.primary_id));
    setFormRestaurantId(String(plan.created_for_restaurant_id || ""));
    setFormPlanName(plan.plan_display_name || "");
    setFormPrice(String(plan.plan_price || ""));
    setFormDuration(String(plan.plan_duration_days || "365"));
    setFormMaxOutlets(String(plan.max_outlets || "1"));
    setFormFeatures(initFeatures(plan));
    setIsEditMode(true);
    setShowCreateModal(true);
  };

  // ─── Open Feature Toggle ─────────────────────────────────────────────
  const handleOpenFeatureToggle = (plan) => {
    setFeaturePlanId(String(plan.primary_id));
    const f = initFeatures(plan);
    setFeatureState({ ...f });
    setOrigFeatures({ ...f });
    setShowFeatureModal(true);
  };

  const changedFeatures = FEATURES.filter(
    (f) => !f.locked && featureState[f.key] !== origFeatures[f.key],
  );

  // ─── Save Custom Plan ────────────────────────────────────────────────
  const handleSaveCustomPlan = async () => {
    if (!formRestaurantId) {
      handleError("Restaurant ID required");
      return;
    }
    if (!formPlanName) {
      handleError("Plan name required");
      return;
    }
    setshowLoaderAdmin(true);
    const fd = new FormData();
    fd.append("main_id", editPlanId);
    fd.append("restaurant_id", formRestaurantId);
    fd.append("plan_display_name", formPlanName);
    fd.append("plan_price", formPrice);
    fd.append("plan_duration_days", formDuration);
    fd.append("plan_start_date", formStartDate);
    fd.append("max_outlets", formMaxOutlets);
    FEATURES.forEach((f) => fd.append(f.key, formFeatures[f.key]));
    await server_post_data(save_update_custom_plan, fd)
      .then((Response) => {
        setshowLoaderAdmin(false);
        if (Response.data.error) {
          handleError(Response.data.message);
          return;
        }
        handleSuccess(Response.data.message);
        setShowCreateModal(false);
        resetForm();
        master_data_get();
      })
      .catch(() => {
        handleError("network");
        setshowLoaderAdmin(false);
      });
  };

  // ─── Update Features ─────────────────────────────────────────────────
  const handleUpdateFeatures = async () => {
    if (changedFeatures.length === 0) {
      handleError("Koi change nahi hua");
      return;
    }
    setshowLoaderAdmin(true);
    const fd = new FormData();
    fd.append("plan_id", featurePlanId);
    changedFeatures.forEach((f) => fd.append(f.key, featureState[f.key]));
    await server_post_data(update_plan_features, fd)
      .then((Response) => {
        setshowLoaderAdmin(false);
        if (Response.data.error) {
          handleError(Response.data.message);
          return;
        }
        handleSuccess(Response.data.message);
        setShowFeatureModal(false);
        master_data_get();
      })
      .catch(() => {
        handleError("network");
        setshowLoaderAdmin(false);
      });
  };

  // ─── Assign Standard Plan ────────────────────────────────────────────
  const handleAssignStandard = async () => {
    if (!assignResId) {
      handleError("Restaurant ID required");
      return;
    }
    setshowLoaderAdmin(true);
    const fd = new FormData();
    fd.append("restaurant_id", assignResId);
    fd.append("plan_name", assignPlanName);
    fd.append("plan_start_date", assignStart);
    await server_post_data(assign_standard_plan, fd)
      .then((Response) => {
        setshowLoaderAdmin(false);
        if (Response.data.error) {
          handleError(Response.data.message);
          return;
        }
        handleSuccess(Response.data.message);
        setShowAssignModal(false);
        setAssignResId("");
        setAssignStart("");
      })
      .catch(() => {
        handleError("network");
        setshowLoaderAdmin(false);
      });
  };

  // ─── Extend Plan ─────────────────────────────────────────────────────
  const handleExtend = async () => {
    if (!extendResId || !extendDays) {
      handleError("Restaurant ID aur days required");
      return;
    }
    setshowLoaderAdmin(true);
    const fd = new FormData();
    fd.append("restaurant_id", extendResId);
    fd.append("extra_days", extendDays);
    await server_post_data(extend_plan_duration, fd)
      .then((Response) => {
        setshowLoaderAdmin(false);
        if (Response.data.error) {
          handleError(Response.data.message);
          return;
        }
        handleSuccess(Response.data.message);
        setShowExtendModal(false);
        setExtendResId("");
        setExtendDays("");
      })
      .catch(() => {
        handleError("network");
        setshowLoaderAdmin(false);
      });
  };

  // ─── Delete ──────────────────────────────────────────────────────────
  const handleDelete = async () => {
    setshowLoaderAdmin(true);
    const fd = new FormData();
    fd.append("plan_id", deletePlanId);
    await server_post_data(delete_custom_plan, fd)
      .then((Response) => {
        setshowLoaderAdmin(false);
        if (Response.data.error) {
          handleError(Response.data.message);
          return;
        }
        handleSuccess(Response.data.message);
        setShowDeleteModal(false);
        master_data_get();
      })
      .catch(() => {
        handleError("network");
        setshowLoaderAdmin(false);
      });
  };

  // ─── Export CSV ──────────────────────────────────────────────────────
  const exportToCSV = () => {
    const headers = [
      "ID",
      "Plan Name",
      "Display Name",
      "Restaurant ID",
      "Price",
      "Duration",
      "Max Outlets",
    ];
    const rows = filteredData.map((p) => [
      p.primary_id,
      p.plan_name,
      p.plan_display_name,
      p.created_for_restaurant_id,
      p.plan_price,
      p.plan_duration_days,
      p.max_outlets,
    ]);
    const csv = [headers, ...rows]
      .map((r) => r.map((c) => `"${c}"`).join(","))
      .join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `plans_${formatDate(new Date())}.csv`;
    a.click();
  };

  // ─── Render helpers ──────────────────────────────────────────────────
  const PlanBadge = ({ name }) => {
    const b = PLAN_BADGE[name] || PLAN_BADGE.custom;
    return (
      <span
        style={{
          padding: "4px 12px",
          borderRadius: "20px",
          fontSize: "12px",
          fontWeight: 600,
          color: b.color,
          backgroundColor: b.bg,
          border: `1px solid ${b.border}`,
        }}
      >
        {b.label}
      </span>
    );
  };

  const FeaturePreview = ({ plan, max = 3 }) => {
    const active = FEATURES.filter((f) => plan[f.key] === 1);
    return (
      <div style={{ display: "flex", flexWrap: "wrap", gap: "4px" }}>
        {active.slice(0, max).map((f) => {
          const b = PLAN_BADGE[f.plan];
          return (
            <span
              key={f.key}
              style={{
                fontSize: "11px",
                padding: "2px 7px",
                borderRadius: "12px",
                background: b.bg,
                color: b.color,
                border: `1px solid ${b.border}`,
              }}
            >
              {f.label}
            </span>
          );
        })}
        {active.length > max && (
          <span style={{ fontSize: "11px", color: "#888" }}>
            +{active.length - max} more
          </span>
        )}
      </div>
    );
  };

  // ─── Render ──────────────────────────────────────────────────────────
  return (
    <div className="dashboard">
      {showLoaderAdmin && <Loader />}
      <div className="dashboard_container">
        <div className="page_content">
          <div className="page_content_container">
            <Header />
            <div className="pageCntn_head">
              <div className="pageNameDiv">
                <p>Plan Management</p>
                <img src={GreyArrow} alt="Arrow" />
              </div>
            </div>

            {/* ── Stats Cards ── */}
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))",
                gap: "15px",
                marginBottom: "20px",
              }}
            >
              {[
                {
                  label: "Total Custom",
                  val: stats.total,
                  grad: "135deg, #667eea 0%, #764ba2 100%",
                  tab: "custom",
                },
                {
                  label: "Free Plans",
                  val: stats.free,
                  grad: "135deg, #43a047 0%, #2e7d32 100%",
                  tab: "standard",
                },
                {
                  label: "Starter",
                  val: stats.starter,
                  grad: "135deg, #ffc107 0%, #ff9800 100%",
                  tab: "custom",
                },
                {
                  label: "Professional",
                  val: stats.professional,
                  grad: "135deg, #1976d2 0%, #0d47a1 100%",
                  tab: "custom",
                },
                {
                  label: "Custom Plans",
                  val: stats.custom,
                  grad: "135deg, #8e24aa 0%, #4a148c 100%",
                  tab: "custom",
                },
              ].map((s) => (
                <div
                  key={s.label}
                  onClick={() => handleTabChange(s.tab)}
                  style={{
                    background: `linear-gradient(${s.grad})`,
                    padding: "20px",
                    borderRadius: "10px",
                    color: "white",
                    cursor: "pointer",
                  }}
                >
                  <div style={{ fontSize: "14px", opacity: 0.9 }}>
                    {s.label}
                  </div>
                  <div style={{ fontSize: "32px", fontWeight: "bold" }}>
                    {s.val}
                  </div>
                </div>
              ))}
            </div>

            {/* ── Filters & Actions ── */}
            <div className="page_body">
              <div
                className="create_new_campaign"
                style={{ marginBottom: "20px" }}
              >
                <div className="dateRange">
                  <div className="d-flex gap-3 align-items-center flex-wrap">
                    <button
                      type="button"
                      className="submitBtn"
                      onClick={() => {
                        resetForm();
                        setShowCreateModal(true);
                      }}
                    >
                      <FaPlus style={{ marginRight: "5px" }} /> Custom Plan
                    </button>
                    <button
                      type="button"
                      className="submitBtn"
                      onClick={() => setShowAssignModal(true)}
                      style={{ backgroundColor: "#28a745" }}
                    >
                      <UserPlus size={16} style={{ marginRight: "5px" }} />{" "}
                      Assign Plan
                    </button>
                    <button
                      type="button"
                      className="submitBtn"
                      onClick={() => setShowExtendModal(true)}
                      style={{ backgroundColor: "#17a2b8" }}
                    >
                      Extend Plan
                    </button>
                    <button
                      type="button"
                      className="submitBtn"
                      onClick={exportToCSV}
                      style={{ backgroundColor: "#6c757d" }}
                    >
                      <Download size={16} style={{ marginRight: "5px" }} />{" "}
                      Export CSV
                    </button>
                  </div>
                </div>
              </div>

              {/* ── Tabs ── */}
              <div
                style={{ display: "flex", gap: "8px", marginBottom: "16px" }}
              >
                {[
                  { key: "custom", label: "Custom Plans" },
                  { key: "standard", label: "Standard Plans" },
                  ...(activeTab === "history"
                    ? [
                        {
                          key: "history",
                          label: `History — Res: ${historyResId}`,
                        },
                      ]
                    : []),
                ].map((t) => (
                  <button
                    key={t.key}
                    type="button"
                    onClick={() => handleTabChange(t.key)}
                    style={{
                      padding: "7px 18px",
                      borderRadius: "20px",
                      border: "none",
                      fontSize: "13px",
                      fontWeight: 600,
                      cursor: "pointer",
                      background:
                        activeTab === t.key
                          ? "var(--primary-color)"
                          : "#f0f0f0",
                      color: activeTab === t.key ? "#fff" : "#555",
                    }}
                  >
                    {t.label}
                  </button>
                ))}
              </div>

              <div className="page_body_container">
                {/* ── Search ── */}
                {activeTab !== "history" && (
                  <div className="searchInput" style={{ marginBottom: "20px" }}>
                    <div className="searchInputDiv">
                      <Search className="searchInputImg" size={20} />
                      <input
                        type="text"
                        value={searchfilter}
                        onChange={handleSearch}
                        placeholder="Search plans..."
                      />
                    </div>
                  </div>
                )}

                {/* ── Table ── */}
                <div className="table_container">
                  <table style={{ width: "100%", borderCollapse: "collapse" }}>
                    <thead>
                      <tr style={{ backgroundColor: "#f8f9fa" }}>
                        <th style={{ padding: "12px", textAlign: "left" }}>
                          #
                        </th>
                        <th style={{ padding: "12px", textAlign: "left" }}>
                          Plan
                        </th>
                        {activeTab !== "standard" && (
                          <th style={{ padding: "12px", textAlign: "left" }}>
                            Restaurant ID
                          </th>
                        )}
                        <th style={{ padding: "12px", textAlign: "left" }}>
                          Price
                        </th>
                        <th style={{ padding: "12px", textAlign: "left" }}>
                          Duration
                        </th>
                        <th style={{ padding: "12px", textAlign: "left" }}>
                          Outlets
                        </th>
                        {activeTab !== "history" && (
                          <th style={{ padding: "12px", textAlign: "left" }}>
                            Features
                          </th>
                        )}
                        {activeTab === "history" && (
                          <>
                            <th style={{ padding: "12px", textAlign: "left" }}>
                              Start
                            </th>
                            <th style={{ padding: "12px", textAlign: "left" }}>
                              End
                            </th>
                            <th style={{ padding: "12px", textAlign: "left" }}>
                              Status
                            </th>
                          </>
                        )}
                        {activeTab !== "history" && (
                          <th style={{ padding: "12px", textAlign: "center" }}>
                            Actions
                          </th>
                        )}
                      </tr>
                    </thead>
                    <tbody>
                      {currentItems.length === 0 && (
                        <tr>
                          <td
                            colSpan={8}
                            style={{
                              textAlign: "center",
                              padding: "40px",
                              color: "#aaa",
                            }}
                          >
                            No data found
                          </td>
                        </tr>
                      )}

                      {/* ── Standard & Custom Rows ── */}
                      {activeTab !== "history" &&
                        currentItems.map((plan, index) => (
                          <tr
                            key={plan.primary_id}
                            style={{ borderBottom: "1px solid #e9ecef" }}
                          >
                            <td style={{ padding: "12px" }}>
                              {indexOfFirst + index + 1}
                            </td>
                            <td style={{ padding: "12px" }}>
                              <PlanBadge name={plan.plan_name} />
                              <div
                                style={{
                                  fontSize: "13px",
                                  color: "#555",
                                  marginTop: "4px",
                                }}
                              >
                                {plan.plan_display_name}
                              </div>
                            </td>
                            {activeTab !== "standard" && (
                              <td style={{ padding: "12px" }}>
                                <span
                                  style={{
                                    padding: "4px 10px",
                                    backgroundColor: "#e7f3ff",
                                    borderRadius: "5px",
                                    fontSize: "12px",
                                  }}
                                >
                                  ID: {plan.created_for_restaurant_id}
                                </span>
                              </td>
                            )}
                            <td style={{ padding: "12px" }}>
                              ₹{plan.plan_price || 0}
                            </td>
                            <td style={{ padding: "12px" }}>
                              {plan.plan_duration_days} days
                            </td>
                            <td style={{ padding: "12px" }}>
                              {plan.max_outlets}
                            </td>
                            <td style={{ padding: "12px" }}>
                              <FeaturePreview plan={plan} />
                            </td>
                            <td
                              style={{ padding: "12px", textAlign: "center" }}
                            >
                              <div
                                style={{
                                  display: "flex",
                                  gap: "6px",
                                  justifyContent: "center",
                                }}
                              >
                                {/* View */}
                                <button
                                  onClick={() => {
                                    setSelectedPlan(plan);
                                    setShowDetailModal(true);
                                  }}
                                  style={{
                                    padding: "6px 12px",
                                    background: "#17a2b8",
                                    color: "#fff",
                                    border: "none",
                                    borderRadius: "5px",
                                    cursor: "pointer",
                                  }}
                                  title="View Details"
                                >
                                  <FaEye />
                                </button>

                                {/* Edit — only custom */}
                                {activeTab === "custom" && (
                                  <button
                                    onClick={() => handleEdit(plan)}
                                    style={{
                                      padding: "6px 12px",
                                      background: "#ffc107",
                                      color: "#fff",
                                      border: "none",
                                      borderRadius: "5px",
                                      cursor: "pointer",
                                    }}
                                    title="Edit Plan"
                                  >
                                    <FaPen />
                                  </button>
                                )}

                                {/* Feature Toggle — only custom */}
                                {activeTab === "custom" && (
                                  <button
                                    onClick={() =>
                                      handleOpenFeatureToggle(plan)
                                    }
                                    style={{
                                      padding: "6px 12px",
                                      background: "#6f42c1",
                                      color: "#fff",
                                      border: "none",
                                      borderRadius: "5px",
                                      cursor: "pointer",
                                    }}
                                    title="Toggle Features"
                                  >
                                    ⚡
                                  </button>
                                )}

                                {/* History — custom */}
                                {activeTab === "custom" && (
                                  <button
                                    onClick={() =>
                                      get_history(
                                        plan.created_for_restaurant_id,
                                      )
                                    }
                                    style={{
                                      padding: "6px 12px",
                                      background: "#28a745",
                                      color: "#fff",
                                      border: "none",
                                      borderRadius: "5px",
                                      cursor: "pointer",
                                    }}
                                    title="View History"
                                  >
                                    <FaHistory />
                                  </button>
                                )}

                                {/* Delete — only custom */}
                                {activeTab === "custom" && (
                                  <button
                                    onClick={() => {
                                      setDeletePlanId(plan.primary_id);
                                      setShowDeleteModal(true);
                                    }}
                                    style={{
                                      padding: "6px 12px",
                                      background: "#dc3545",
                                      color: "#fff",
                                      border: "none",
                                      borderRadius: "5px",
                                      cursor: "pointer",
                                    }}
                                    title="Delete"
                                  >
                                    🗑
                                  </button>
                                )}
                              </div>
                            </td>
                          </tr>
                        ))}

                      {/* ── History Rows ── */}
                      {activeTab === "history" &&
                        currentItems.map((h, index) => (
                          <tr
                            key={h.primary_id}
                            style={{ borderBottom: "1px solid #e9ecef" }}
                          >
                            <td style={{ padding: "12px" }}>
                              {indexOfFirst + index + 1}
                            </td>
                            <td style={{ padding: "12px" }}>
                              <PlanBadge name={h.plan_name} />
                              <div
                                style={{
                                  fontSize: "12px",
                                  color: "#777",
                                  marginTop: "3px",
                                }}
                              >
                                {h.plan_display_name}
                              </div>
                            </td>
                            <td style={{ padding: "12px" }}>
                              ₹{h.plan_price || 0}
                            </td>
                            <td style={{ padding: "12px" }}>
                              {h.plan_duration_days || "—"} days
                            </td>
                            <td style={{ padding: "12px" }}>
                              {h.max_outlets || "—"}
                            </td>
                            <td style={{ padding: "12px", fontSize: "13px" }}>
                              {h.plan_start_date || "—"}
                            </td>
                            <td style={{ padding: "12px", fontSize: "13px" }}>
                              {h.plan_end_date || "—"}
                            </td>
                            <td style={{ padding: "12px" }}>
                              {h.plan_expired ? (
                                <span
                                  style={{
                                    color: "#c62828",
                                    fontSize: "12px",
                                    fontWeight: 600,
                                  }}
                                >
                                  Expired
                                </span>
                              ) : h.is_active === 1 ? (
                                <span
                                  style={{
                                    color: "#2e7d32",
                                    fontSize: "12px",
                                    fontWeight: 600,
                                  }}
                                >
                                  Active
                                </span>
                              ) : (
                                <span
                                  style={{ color: "#888", fontSize: "12px" }}
                                >
                                  Inactive
                                </span>
                              )}
                            </td>
                          </tr>
                        ))}
                    </tbody>
                  </table>
                </div>

                {/* ── Pagination ── */}
                <div
                  className="pagination-container"
                  style={{ marginTop: "20px" }}
                >
                  <Pagination>
                    <Pagination.First
                      onClick={() => setCurrentPage(1)}
                      disabled={currentPage === 1}
                    />
                    <Pagination.Prev
                      onClick={() => setCurrentPage((p) => p - 1)}
                      disabled={currentPage === 1}
                    />
                    {[...Array(totalPages)].map((_, i) => (
                      <Pagination.Item
                        key={i + 1}
                        active={i + 1 === currentPage}
                        onClick={() => setCurrentPage(i + 1)}
                      >
                        {i + 1}
                      </Pagination.Item>
                    ))}
                    <Pagination.Next
                      onClick={() => setCurrentPage((p) => p + 1)}
                      disabled={currentPage === totalPages}
                    />
                    <Pagination.Last
                      onClick={() => setCurrentPage(totalPages)}
                      disabled={currentPage === totalPages}
                    />
                  </Pagination>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ══════════════════════════════════════════════════════════
          MODAL 1 — Create / Edit Custom Plan
      ══════════════════════════════════════════════════════════ */}
      <Modal
        show={showCreateModal}
        onHide={() => {
          setShowCreateModal(false);
          resetForm();
        }}
        centered
        backdrop="static"
        size="lg"
      >
        <Modal.Header closeButton>
          <Modal.Title>
            {isEditMode ? "Edit Custom Plan" : "Create Custom Plan"}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="row" style={{ gap: "0" }}>
            <div className="col-md-4" style={{ marginBottom: "12px" }}>
              <label style={{ fontWeight: 500, fontSize: "13px" }}>
                Restaurant <span style={{ color: "red" }}>*</span>
              </label>
              <select
                className="form-select"
                value={formRestaurantId}
                onChange={(e) => setFormRestaurantId(e.target.value)}
              >
                <option value="">-- Select Restaurant --</option>
                {restaurantOptions.map((r) => (
                  <option key={r.id} value={r.id}>
                    {r.name} (ID: {r.id})
                  </option>
                ))}
              </select>
            </div>
            <div className="col-md-4" style={{ marginBottom: "12px" }}>
              <label style={{ fontWeight: 500, fontSize: "13px" }}>
                Plan Display Name <span style={{ color: "red" }}>*</span>
              </label>
              <input
                type="text"
                className="form-control"
                placeholder="e.g. Cafe Mocha Special"
                value={formPlanName}
                onChange={(e) => setFormPlanName(e.target.value)}
              />
            </div>
            <div className="col-md-4" style={{ marginBottom: "12px" }}>
              <label style={{ fontWeight: 500, fontSize: "13px" }}>
                Price (₹)
              </label>
              <input
                type="number"
                className="form-control"
                placeholder="e.g. 7500"
                value={formPrice}
                onChange={(e) => setFormPrice(e.target.value)}
              />
            </div>
            <div className="col-md-4" style={{ marginBottom: "12px" }}>
              <label style={{ fontWeight: 500, fontSize: "13px" }}>
                Duration (days)
              </label>
              <input
                type="number"
                className="form-control"
                placeholder="365"
                value={formDuration}
                onChange={(e) => setFormDuration(e.target.value)}
              />
            </div>
            <div className="col-md-4" style={{ marginBottom: "12px" }}>
              <label style={{ fontWeight: 500, fontSize: "13px" }}>
                Start Date
              </label>
              <input
                type="date"
                className="form-control"
                value={formStartDate}
                onChange={(e) => setFormStartDate(e.target.value)}
              />
            </div>
            <div className="col-md-4" style={{ marginBottom: "12px" }}>
              <label style={{ fontWeight: 500, fontSize: "13px" }}>
                Max Outlets
              </label>
              <input
                type="number"
                className="form-control"
                placeholder="1"
                value={formMaxOutlets}
                onChange={(e) => setFormMaxOutlets(e.target.value)}
              />
            </div>

            {/* Features */}
            <div className="col-md-12" style={{ marginTop: "8px" }}>
              <label
                style={{
                  fontWeight: 600,
                  fontSize: "14px",
                  marginBottom: "10px",
                  display: "block",
                }}
              >
                Select Features
              </label>
              {["free", "starter", "professional"].map((planType) => (
                <div key={planType} style={{ marginBottom: "14px" }}>
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "8px",
                      marginBottom: "8px",
                    }}
                  >
                    <span
                      style={{
                        background: PLAN_BADGE[planType].bg,
                        color: PLAN_BADGE[planType].color,
                        border: `1px solid ${PLAN_BADGE[planType].border}`,
                        borderRadius: "6px",
                        padding: "2px 12px",
                        fontSize: "12px",
                        fontWeight: 700,
                      }}
                    >
                      {PLAN_BADGE[planType].label}
                    </span>
                    <span style={{ fontSize: "12px", color: "#888" }}>
                      {
                        FEATURES.filter(
                          (f) =>
                            f.plan === planType && formFeatures[f.key] === 1,
                        ).length
                      }{" "}
                      /{FEATURES.filter((f) => f.plan === planType).length}
                    </span>
                  </div>
                  <div className="row" style={{ margin: "0" }}>
                    {FEATURES.filter((f) => f.plan === planType).map((f) => {
                      const isOn = formFeatures[f.key] === 1;
                      const b = PLAN_BADGE[f.plan];
                      return (
                        <div
                          key={f.key}
                          className="col-md-4 col-6"
                          style={{ padding: "3px" }}
                          onClick={() =>
                            !f.locked &&
                            setFormFeatures((prev) => ({
                              ...prev,
                              [f.key]: prev[f.key] ? 0 : 1,
                            }))
                          }
                        >
                          <div
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: "8px",
                              padding: "8px 10px",
                              borderRadius: "7px",
                              cursor: f.locked ? "default" : "pointer",
                              border: `1.5px solid ${isOn ? b.border : "#e0e0e0"}`,
                              background: isOn ? b.bg : "#fafafa",
                              transition: "all 0.15s",
                              userSelect: "none",
                            }}
                          >
                            <div
                              style={{
                                width: "16px",
                                height: "16px",
                                borderRadius: "4px",
                                flexShrink: 0,
                                border: `2px solid ${isOn ? b.color : "#bbb"}`,
                                background: isOn ? b.color : "#fff",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                              }}
                            >
                              {isOn && (
                                <span
                                  style={{
                                    color: "#fff",
                                    fontSize: "10px",
                                    fontWeight: 700,
                                  }}
                                >
                                  ✓
                                </span>
                              )}
                            </div>
                            <span
                              style={{
                                fontSize: "12px",
                                fontWeight: 500,
                                color: "#333",
                              }}
                            >
                              {f.label}
                            </span>
                            {f.locked && (
                              <span
                                style={{
                                  marginLeft: "auto",
                                  fontSize: "10px",
                                  background: "#f0f0f0",
                                  color: "#999",
                                  borderRadius: "4px",
                                  padding: "1px 5px",
                                }}
                              >
                                Fixed
                              </span>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button
            className="logoutNoBtn"
            onClick={() => {
              setShowCreateModal(false);
              resetForm();
            }}
          >
            Cancel
          </Button>
          <Button className="logoutYesBtn" onClick={handleSaveCustomPlan}>
            {isEditMode ? "Update Plan" : "Save & Assign"}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* ══════════════════════════════════════════════════════════
          MODAL 2 — Feature Toggle
      ══════════════════════════════════════════════════════════ */}
      <Modal
        show={showFeatureModal}
        onHide={() => setShowFeatureModal(false)}
        centered
        backdrop="static"
        size="lg"
      >
        <Modal.Header closeButton>
          <Modal.Title>
            Toggle Features
            {changedFeatures.length > 0 && (
              <span
                style={{
                  marginLeft: "10px",
                  fontSize: "13px",
                  background: "#ff6f00",
                  color: "#fff",
                  borderRadius: "6px",
                  padding: "2px 10px",
                }}
              >
                {changedFeatures.length} changed
              </span>
            )}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {["free", "starter", "professional"].map((planType) => (
            <div key={planType} style={{ marginBottom: "16px" }}>
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                  marginBottom: "8px",
                }}
              >
                <span
                  style={{
                    background: PLAN_BADGE[planType].bg,
                    color: PLAN_BADGE[planType].color,
                    border: `1px solid ${PLAN_BADGE[planType].border}`,
                    borderRadius: "6px",
                    padding: "2px 12px",
                    fontSize: "12px",
                    fontWeight: 700,
                  }}
                >
                  {PLAN_BADGE[planType].label}
                </span>
              </div>
              <div className="row" style={{ margin: "0" }}>
                {FEATURES.filter((f) => f.plan === planType).map((f) => {
                  const isOn = featureState[f.key] === 1;
                  const changed =
                    !f.locked && featureState[f.key] !== origFeatures[f.key];
                  const b = PLAN_BADGE[f.plan];
                  return (
                    <div
                      key={f.key}
                      className="col-md-4 col-6"
                      style={{ padding: "3px" }}
                      onClick={() =>
                        !f.locked &&
                        setFeatureState((prev) => ({
                          ...prev,
                          [f.key]: prev[f.key] ? 0 : 1,
                        }))
                      }
                    >
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "8px",
                          padding: "9px 10px",
                          borderRadius: "7px",
                          cursor: f.locked ? "default" : "pointer",
                          border: `1.5px solid ${changed ? "#ff6f00" : isOn ? b.border : "#e0e0e0"}`,
                          background: changed
                            ? "#fff8f0"
                            : isOn
                              ? b.bg
                              : "#fafafa",
                          transition: "all 0.15s",
                          userSelect: "none",
                        }}
                      >
                        {/* Toggle switch */}
                        <div
                          style={{
                            width: "32px",
                            height: "18px",
                            borderRadius: "9px",
                            flexShrink: 0,
                            background: isOn
                              ? f.locked
                                ? "#aaa"
                                : b.color
                              : "#ddd",
                            position: "relative",
                            transition: "background 0.2s",
                          }}
                        >
                          <div
                            style={{
                              position: "absolute",
                              top: "2px",
                              left: isOn ? "16px" : "2px",
                              width: "14px",
                              height: "14px",
                              borderRadius: "50%",
                              background: "#fff",
                              transition: "left 0.2s",
                              boxShadow: "0 1px 2px #0003",
                            }}
                          />
                        </div>
                        <span
                          style={{
                            fontSize: "12px",
                            fontWeight: 500,
                            color: "#333",
                          }}
                        >
                          {f.label}
                        </span>
                        {changed && (
                          <span
                            style={{
                              marginLeft: "auto",
                              fontSize: "10px",
                              background: "#ff6f00",
                              color: "#fff",
                              borderRadius: "4px",
                              padding: "1px 5px",
                            }}
                          >
                            Changed
                          </span>
                        )}
                        {f.locked && (
                          <span
                            style={{
                              marginLeft: "auto",
                              fontSize: "10px",
                              background: "#f0f0f0",
                              color: "#999",
                              borderRadius: "4px",
                              padding: "1px 5px",
                            }}
                          >
                            Fixed
                          </span>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </Modal.Body>
        <Modal.Footer>
          <Button
            className="logoutNoBtn"
            onClick={() => {
              setFeatureState({ ...origFeatures });
            }}
          >
            Reset
          </Button>
          <Button
            className="logoutNoBtn"
            onClick={() => setShowFeatureModal(false)}
          >
            Cancel
          </Button>
          <Button className="logoutYesBtn" onClick={handleUpdateFeatures}>
            Save{" "}
            {changedFeatures.length > 0 ? `(${changedFeatures.length})` : ""}{" "}
            Changes
          </Button>
        </Modal.Footer>
      </Modal>

      {/* ══════════════════════════════════════════════════════════
          MODAL 3 — Assign Standard Plan
      ══════════════════════════════════════════════════════════ */}
      <Modal
        show={showAssignModal}
        onHide={() => setShowAssignModal(false)}
        centered
        backdrop="static"
      >
        <Modal.Header closeButton>
          <Modal.Title>Assign Standard Plan</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div
            style={{ display: "flex", flexDirection: "column", gap: "14px" }}
          >
            <div>
              <label style={{ fontWeight: 500, fontSize: "13px" }}>
                Restaurant <span style={{ color: "red" }}>*</span>
              </label>
              <select
                className="form-select"
                value={assignResId}
                onChange={(e) => setAssignResId(e.target.value)}
              >
                <option value="">-- Select Restaurant --</option>
                {restaurantOptions.map((r) => (
                  <option key={r.id} value={r.id}>
                    {r.name} (ID: {r.id})
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label style={{ fontWeight: 500, fontSize: "13px" }}>
                Plan <span style={{ color: "red" }}>*</span>
              </label>
              <select
                className="form-select"
                value={assignPlanName}
                onChange={(e) => setAssignPlanName(e.target.value)}
              >
                <option value="free">Free</option>
                <option value="starter">Starter</option>
                <option value="professional">Professional</option>
              </select>
            </div>
            <div>
              <label style={{ fontWeight: 500, fontSize: "13px" }}>
                Start Date
              </label>
              <input
                type="date"
                className="form-control"
                value={assignStart}
                onChange={(e) => setAssignStart(e.target.value)}
              />
            </div>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button
            className="logoutNoBtn"
            onClick={() => setShowAssignModal(false)}
          >
            Cancel
          </Button>
          <Button className="logoutYesBtn" onClick={handleAssignStandard}>
            Assign Plan
          </Button>
        </Modal.Footer>
      </Modal>

      {/* ══════════════════════════════════════════════════════════
          MODAL 4 — Extend Plan
      ══════════════════════════════════════════════════════════ */}
      <Modal
        show={showExtendModal}
        onHide={() => setShowExtendModal(false)}
        centered
        backdrop="static"
      >
        <Modal.Header closeButton>
          <Modal.Title>Extend Plan Duration</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div
            style={{ display: "flex", flexDirection: "column", gap: "14px" }}
          >
            <div>
              <label style={{ fontWeight: 500, fontSize: "13px" }}>
                Restaurant <span style={{ color: "red" }}>*</span>
              </label>
              <select
                className="form-select"
                value={extendResId}
                onChange={(e) => setExtendResId(e.target.value)}
              >
                <option value="">-- Select Restaurant --</option>
                {restaurantOptions.map((r) => (
                  <option key={r.id} value={r.id}>
                    {r.name} (ID: {r.id})
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label style={{ fontWeight: 500, fontSize: "13px" }}>
                Extra Days <span style={{ color: "red" }}>*</span>
              </label>
              <input
                type="number"
                className="form-control"
                placeholder="e.g. 30"
                value={extendDays}
                onChange={(e) => setExtendDays(e.target.value)}
              />
            </div>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button
            className="logoutNoBtn"
            onClick={() => setShowExtendModal(false)}
          >
            Cancel
          </Button>
          <Button className="logoutYesBtn" onClick={handleExtend}>
            Extend Plan
          </Button>
        </Modal.Footer>
      </Modal>

      {/* ══════════════════════════════════════════════════════════
          MODAL 5 — Plan Detail View
      ══════════════════════════════════════════════════════════ */}
      <Modal
        show={showDetailModal}
        onHide={() => setShowDetailModal(false)}
        centered
        backdrop="static"
        size="lg"
      >
        <Modal.Header closeButton>
          <Modal.Title>Plan Details</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedPlan && (
            <div>
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "1fr 1fr",
                  gap: "16px",
                  marginBottom: "16px",
                }}
              >
                {[
                  {
                    label: "Plan Name",
                    val: <PlanBadge name={selectedPlan.plan_name} />,
                  },
                  {
                    label: "Display Name",
                    val: selectedPlan.plan_display_name,
                  },
                  {
                    label: "Restaurant ID",
                    val: selectedPlan.created_for_restaurant_id || "—",
                  },
                  { label: "Price", val: `₹${selectedPlan.plan_price || 0}` },
                  {
                    label: "Duration",
                    val: `${selectedPlan.plan_duration_days} days`,
                  },
                  { label: "Max Outlets", val: selectedPlan.max_outlets },
                ].map((item) => (
                  <div key={item.label}>
                    <strong style={{ color: "#495057", fontSize: "13px" }}>
                      {item.label}
                    </strong>
                    <div style={{ marginTop: "4px", fontSize: "14px" }}>
                      {item.val}
                    </div>
                  </div>
                ))}
              </div>
              <strong style={{ color: "#495057", fontSize: "13px" }}>
                Active Features
              </strong>
              <div
                style={{
                  display: "flex",
                  flexWrap: "wrap",
                  gap: "6px",
                  marginTop: "8px",
                }}
              >
                {FEATURES.filter((f) => selectedPlan[f.key] === 1).map((f) => {
                  const b = PLAN_BADGE[f.plan];
                  return (
                    <span
                      key={f.key}
                      style={{
                        fontSize: "12px",
                        padding: "4px 10px",
                        borderRadius: "12px",
                        background: b.bg,
                        color: b.color,
                        border: `1px solid ${b.border}`,
                        fontWeight: 500,
                      }}
                    >
                      {f.label}
                    </span>
                  );
                })}
              </div>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button
            className="logoutNoBtn"
            onClick={() => setShowDetailModal(false)}
          >
            Close
          </Button>
        </Modal.Footer>
      </Modal>

      {/* ══════════════════════════════════════════════════════════
          MODAL 6 — Delete Confirm
      ══════════════════════════════════════════════════════════ */}
      <Modal
        show={showDeleteModal}
        className="confirmModal"
        centered
        backdrop="static"
        keyboard={false}
        onHide={() => setShowDeleteModal(false)}
      >
        <Modal.Header className="confirmModalHeader" closeButton></Modal.Header>
        <Modal.Body className="confirmBody">
          <p className="modalText">Do You Want to Delete This Custom Plan?</p>
        </Modal.Body>
        <Modal.Footer className="confirmModalFooter">
          <Button
            className="logoutNoBtn"
            onClick={() => setShowDeleteModal(false)}
          >
            No
          </Button>
          <Button className="logoutYesBtn" onClick={handleDelete}>
            Yes, Delete
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
}

export default PlanManagement;
